import { useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

/**
 * Content Writing Webhook Parameters
 * Includes ALL fields from content_briefs table for complete content idea data
 */
export interface ContentWritingWebhookParams {
  id: string
  title: string | null
  description: string | null  // This is the key field (NOT meta_description)
  primary_keyword: string | null
  secondary_keywords: string | null
  category: string | null
  content_type: string | null
  article_type: string | null
  cluster_name: string | null
  detected_industry: string | null
  content_focus: string | null
  estimated_word_count: string | null
  keyword_integration_notes: string | null
  related_terms: string | null
  semantic_variations: string | null
  question_variations: string | null
  target_audience: string | null
  content_outline: string | null
  recommendations: string | null
  workspace_id: string
  user_id: string
  // Additional fields that might be present
  url?: string | null
  status?: string | null
  content_writing_requested_at?: string | null
  content_generation_queue_id?: string | null
  assigned_writer_id?: string | null
  internal_links?: string[] | null
  external_references?: string[] | null
  last_updated_at?: string | null
  content_priority?: number | null
  seo_score?: number | null
  publish_date?: string | null
  content_tags?: string[] | null
  hierarchy_position?: number | null
  total_content_pieces?: number | null
  content_piece_index?: number | null
  meta_description?: string | null
  content_format?: string | null
  content_function?: string | null
}

export interface ContentWritingWebhookResponse {
  success: boolean
  message?: string
  article_id?: string
  error?: string
}

export interface UseContentWritingWebhookReturn {
  triggerContentWriting: (contentIdea: any, workspaceId: string) => Promise<ContentWritingWebhookResponse>
  isLoading: boolean
  error: string | null
}

/**
 * Hook for triggering content writing webhook
 * Sends complete content idea data to VITE_CONTENT_WRITING_WEBHOOK
 */
export const useContentWritingWebhook = (): UseContentWritingWebhookReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { userId } = useAuth();

  const triggerContentWriting = useCallback(async (
    contentIdea: any, 
    workspaceId: string
  ): Promise<ContentWritingWebhookResponse> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const webhookUrl = import.meta.env.VITE_N8N_CONTENT_WRITING_WEBHOOK_URL;
      
      if (!webhookUrl) {
        throw new Error('Content writing webhook URL not configured. Please set VITE_N8N_CONTENT_WRITING_WEBHOOK_URL environment variable.');
      }

      if (!userId) {
        throw new Error('User not authenticated');
      }

      // Send ALL fields from content_briefs table
      const payload: ContentWritingWebhookParams = {
        id: contentIdea.id || `temp_${Date.now()}`,
        title: contentIdea.title || null,
        description: contentIdea.description || null, // Key field - description not meta_description
        primary_keyword: contentIdea.primary_keyword || null,
        secondary_keywords: contentIdea.secondary_keywords || null,
        category: contentIdea.category || null,
        content_type: contentIdea.content_type || null,
        article_type: contentIdea.article_type || null,
        cluster_name: contentIdea.cluster_name || null,
        detected_industry: contentIdea.detected_industry || null,
        content_focus: contentIdea.content_focus || null,
        estimated_word_count: contentIdea.estimated_word_count || null,
        keyword_integration_notes: contentIdea.keyword_integration_notes || null,
        related_terms: contentIdea.related_terms || null,
        semantic_variations: contentIdea.semantic_variations || null,
        question_variations: contentIdea.question_variations || null,
        target_audience: contentIdea.target_audience || null,
        content_outline: contentIdea.content_outline || null,
        recommendations: contentIdea.recommendations || null,
        workspace_id: workspaceId,
        user_id: userId,
        // Optional fields
        url: contentIdea.url || null,
        status: contentIdea.status || null,
        content_writing_requested_at: contentIdea.content_writing_requested_at || null,
        content_generation_queue_id: contentIdea.content_generation_queue_id || null,
        assigned_writer_id: contentIdea.assigned_writer_id || null,
        internal_links: contentIdea.internal_links || null,
        external_references: contentIdea.external_references || null,
        last_updated_at: contentIdea.last_updated_at || null,
        content_priority: contentIdea.content_priority || null,
        seo_score: contentIdea.seo_score || null,
        publish_date: contentIdea.publish_date || null,
        content_tags: contentIdea.content_tags || null,
        hierarchy_position: contentIdea.hierarchy_position || null,
        total_content_pieces: contentIdea.total_content_pieces || null,
        content_piece_index: contentIdea.content_piece_index || null,
        meta_description: contentIdea.meta_description || null,
        content_format: contentIdea.content_format || null,
        content_function: contentIdea.content_function || null,
      };

      console.log('[useContentWritingWebhook] Triggering content writing with payload:', payload);

      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Webhook request failed: ${response.status} ${response.statusText}`);
      }

      const result: ContentWritingWebhookResponse = await response.json();

      if (result.success) {
        toast.success('Content writing started successfully!', {
          description: result.message || 'Your article will be generated shortly.'
        });
      } else {
        throw new Error(result.error || 'Content writing webhook returned failure');
      }

      console.log('[useContentWritingWebhook] Content writing webhook successful:', result);
      return result;

    } catch (err: any) {
      const errorMessage = err.message || 'Failed to start content writing';
      console.error('[useContentWritingWebhook] Content writing webhook error:', err);
      
      setError(errorMessage);
      toast.error('Failed to start content writing', {
        description: errorMessage
      });
      
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  return {
    triggerContentWriting,
    isLoading,
    error
  };
};